import { getAuthHeaders, getAxios } from '@/helpers/axios-helper';

import { LoginProviderByFirebaseProviderId } from '@/enums/login';
import { ModalHelper } from '@/helpers/modal-helper';
import { SnowplowContext, SnowplowLoginContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { LoginMessageModal } from '@/lazy-load-components';

import { UserAccountEvents } from '@/enums/events';
import { AccountModes, LoginProvider } from '@/enums/login';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';
import ToastHelper from '@/helpers/toast/toast-helper';
import { Store } from 'vuex';

import LocalForage from 'localforage';
import { getVm } from './vm-helper';
import {
	EmailAuthProvider,
	OAuthProvider,
	getAuth,
	sendPasswordResetEmail,
	signInWithCustomToken,
} from 'firebase/auth';

type AuthConsents = {
	acceptedPrivacyPolicy: boolean;
	acceptedEmailAgreement: boolean;
	mode: string;
	location: string;
};

interface ExchangeTokenResponse {
	firebase_token: string;
}

const UserHelper = {
	currentUser(): Promise<any> {
		return new Promise((resolve, reject) => {
			const auth = getAuth();
			if (auth.currentUser) {
				resolve(auth.currentUser);
			}
			const unsubscribe = auth.onAuthStateChanged(user => {
				unsubscribe();
				resolve(user);
			}, reject);
		});
	},

	async generateToken(handleLogout = true) {
		try {
			const user = await this.currentUser();

			if (!user) {
				throw new Error('Error getting firebase user');
			}

			const token = await user.getIdToken();
			return token;
		} catch (error) {
			// Logout if we can't generate the token.
			const err: any = error;
			const vm = getVm();

			captureMessageForSentry(
				'Refresh token Issue:',
				{ err, where: '[user-helper] generateToken' },
				Sentry.Severity.Error
			);

			if (vm && handleLogout) {
				await vm.$store.dispatch('user/logout');
				ToastHelper.error({
					message: vm.$t('WEBAPP_REFRESH_TOKEN_ERROR') as string,
					duration: 4000,
				});
			}
		}
	},

	async reauthenticateSocialAuth(provider: string): Promise<boolean> {
		try {
			const user = await this.currentUser();
			if (!user) return false;
			await user.reauthenticateWithPopup(new OAuthProvider(LoginProviderByFirebaseProviderId[provider]));
			return true;
		} catch (error) {
			return false;
		}
	},

	async reauthenticateUserWithPassword(password: string) {
		try {
			const user = await this.currentUser();
			if (!user) return false;
			const credential = EmailAuthProvider.credential(user.email, password);
			await user.reauthenticateWithCredential(credential);
			return true;
		} catch (error) {
			return false;
		}
	},

	async logout(token: string | undefined, justwatchId: string | undefined) {
		if (!token && !justwatchId) {
			console.warn('Trying to logout without jwId');
			return;
		}

		const headers = await getAuthHeaders(token, { ['DEVICE-ID']: justwatchId });
		return getAxios().post(`${JW_CONFIG.IDENTITY_URL}/sign_out`, {}, { headers });
	},

	async requestPasswordReset(payload: { email: string; url: string; handleCodeInApp: boolean }): Promise<any> {
		return sendPasswordResetEmail(getAuth(), payload.email, {
			url: payload.url,
			handleCodeInApp: payload.handleCodeInApp,
		});
	},

	async sendCode(code: string, token: string): Promise<any> {
		const headers = await getAuthHeaders(token);
		return getAxios().post(`${JW_CONFIG.USER_URL}/tv/connect`, { code }, { headers });
	},

	authenticateUser: async (store: Store<any>) => {
		const AUTH_CONSENTS = 'AUTH_CONSENTS';

		try {
			// If this succeeds, then it means the user is registered internally (meaning the operation is a login operation)
			await store.dispatch('user/verifyRegistration');

			// Login operation
			try {
				const authConsents: AuthConsents | null = await LocalForage.getItem(AUTH_CONSENTS);
				await store.dispatch('user/signInInternally');
				const contexts: SnowplowContext[] = [new SnowplowLoginContext(store.state.user.jwLoginId)];
				TrackingHelper.trackEvent(
					'user_account',
					{
						action: UserAccountEvents.LOGIN,
						property: authConsents?.location
							? `${store.state.user.info.loginProvider}_${authConsents.location}`
							: store.state.user.info.loginProvider,
						label: store.state.user.jwLoginId,
					},
					contexts
				);

				await LocalForage.removeItem(AUTH_CONSENTS);
				await ModalHelper.openModal(LoginMessageModal, { successType: 'logged-in', closeDelay: 2000 });
			} catch (err) {
				// If we can't sign in with Firebase SDK then we logout the user (jw-app state)
				const error: any = err;
				await store.dispatch('user/logout');

				TrackingHelper.trackEvent('user_account', {
					action: UserAccountEvents.LOGIN_ERROR,
					property: store.state.user.info.loginProvider,
					label: error,
				});
				ModalHelper.openModal(LoginMessageModal, { successType: 'login-failed', closeDelay: 2000 });
			}
		} catch {
			// SIGN UP
			// User is not yet registered with our internal backend.
			try {
				await store.dispatch('user/registerInternally', false);
				const authConsents: AuthConsents | null = await LocalForage.getItem(AUTH_CONSENTS);
				if (authConsents) {
					const consents = store.getters['user/consents'];
					// If the user creates account with the SIGN_IN modal, we want to save the user consents as true and assume they gave consent
					await store.dispatch('user/saveSettings', {
						jw_consents: {
							...consents,
							emails:
								authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedEmailAgreement : true,
							terms_and_conditions:
								authConsents.mode === AccountModes.SIGN_UP ? authConsents.acceptedPrivacyPolicy : true,
						},
					});

					const contexts: SnowplowContext[] = [new SnowplowLoginContext(store.state.user.jwLoginId)];
					TrackingHelper.trackEvent(
						'useraccount',
						{
							action: 'account_settings_submitted',
							label: 'data_protection',
							property: 'settings',
						},
						contexts
					);

					TrackingHelper.trackEvent(
						'useraccount',
						{
							action: 'account_settings_submitted',
							label: 'interest_emails',
							property: 'settings',
							value:
								authConsents.acceptedEmailAgreement == true ||
								authConsents.mode === AccountModes.SIGN_IN
									? 1
									: 0,
						},
						contexts
					);

					TrackingHelper.trackEvent(
						'user_account',
						{
							action: UserAccountEvents.SIGNUP,
							property: authConsents?.location
								? `${store.state.user.info.loginProvider}_${authConsents.location}`
								: store.state.user.info.loginProvider,
							label: store.state.user.jwLoginId,
						},
						contexts
					);

					await LocalForage.removeItem(AUTH_CONSENTS);
				}

				await ModalHelper.openModal(LoginMessageModal, { successType: 'logged-in', closeDelay: 2000 });
			} catch (err) {
				const error: any = err;

				// If the registration in consumer-api fails, we just want to delete the user in firebase and then show a failure message.
				await store.dispatch('user/deleteAccountPermanently');
				ModalHelper.openModal(LoginMessageModal, { successType: 'sign-up-failed', closeDelay: 2000 });
				TrackingHelper.trackEvent('user_account', {
					action: UserAccountEvents.SIGNUP_ERROR,
					property: store.state.user.info.loginProvider,
					label: error,
				});
			}
		}
	},

	async exchangeToken(store: Store<any>): Promise<boolean> {
		// Check if it's already a Firebase token, only try exchange if it isn't.
		try {
			const res = await getAxios().post<ExchangeTokenResponse>(
				`${JW_CONFIG.IDENTITY_URL}/exchange_token`,
				{},
				{
					headers: {
						Authorization: 'Bearer ' + store.state.user.accessToken,
						['DEVICE-ID']: store.state.user.jwId,
					},
				}
			);

			// If exchange_token is succesful we need to register the new firebase_token with firebase SDK
			// to make it consistent with local state
			if (res.status !== 204) {
				try {
					await signInWithCustomToken(getAuth(), res.data.firebase_token);
				} catch (err) {
					await store.dispatch('user/logout');

					// Only logging sentry error, if this fails we can use the normal token and it should still work fine.
					const error: any = err;
					captureMessageForSentry(
						'Custom Token Signin Error',
						{ error, where: '[startup.vue]' },
						Sentry.Severity.Error
					);

					return false;
				}
				return true;
			} else {
				// Throw error so we can logout the user if a firebase token is in state and firebase user is empty
				throw Error('Firebase token detected when trying to exchange token');
			}
		} catch (err) {
			// We probably have a firebase token that is expired and we don't have a firebase user for some reason.
			// This is a very weird state for the user to be in, because they can't sign out as well until they clear the app data on their browser.
			await store.dispatch('user/logout');

			const error: any = err;
			captureMessageForSentry('Exchange Token Error', { error, where: '[startup.vue]' }, Sentry.Severity.Error);
			return false;
		}
	},
};

export default UserHelper;
